import { FormArray, FormGroup } from '@angular/forms';
import { firstValueFrom, Observable } from 'rxjs';

import { Sales_order } from '@app/shared/_models/sales';
import { DocType, EVENTTYP_INSERT, EVENTTYP_UPDATE } from '@app/shared/constants';
import { SalesOrderState } from '@app/shared/constants-server';
import { prepareRentalSave } from './rental-contract.save';

// Commande de vente (F2) d'un dossier de location (JP 30/10) : les produits vendus (forfait, matelas, produits liés) n'entrent plus au dossier.
// Même client (payeur), mêmes adresses, même bénéficiaire et même poste que le dossier ; jamais sa FSE ; née en création, validée par le passage qui la livre.
export interface SaleOrderHead { id: number; customer: number; station: number | null; addr_deliv: number; addr_bill: number; recipient: number | null; }

// En-tête lu sur la valeur brute du formulaire du dossier (getRawValue).
export function saleOrderHeadOf(dossier: any, id = Number(dossier?.id) || 0): SaleOrderHead {
  const r = Number(dossier?.data?.recipient);
  return {
    id, customer: Number(dossier?.customer) || 0, station: dossier?.station ?? null,
    addr_deliv: Number(dossier?.addr_deliv) || 0, addr_bill: Number(dossier?.addr_bill) || 0,
    recipient: Number.isInteger(r) && r > 0 && r !== Number(dossier?.customer) ? r : null,
  };
}

// CREATED (audit 31/10 #27) : une F2 validée avant la confirmation du passage restait orpheline, créance posée, si la fenêtre se fermait.
export function newSaleOrderForm(head: SaleOrderHead, user: number | null, store: number | null, lines: FormGroup[]): FormGroup {
  const fg = Sales_order.newOrderFormGroup();
  fg.patchValue({
    doc_type: DocType.FACTURE, state: SalesOrderState.CREATED, customer: head.customer, station: head.station, id_user: user, store,
    addr_deliv: head.addr_deliv, addr_bill: head.addr_bill, data: { rental_order_id: head.id, ...(head.recipient ? { recipient: head.recipient } : {}) },
  });
  const arr = fg.get('lines') as FormArray;
  lines.forEach((l, i) => { l.patchValue({ id: 0, id_order: 0, seq: i + 1 }); arr.push(l); });
  return fg;
}

// INSERT de la commande de vente ; rend son id, ou lève l'erreur du serveur avec son message.
export async function createSaleOrder(post: (fg: FormGroup) => Observable<any>, head: SaleOrderHead, user: number | null, store: number | null, lines: FormGroup[]): Promise<number> {
  const response: any = await firstValueFrom(post(newSaleOrderForm(head, user, store, lines)));
  const id = Number(response?.data?.order_id);
  if (!response?.success || !id) throw new Error(response?.msg || response?.error_msg || 'commande de vente non créée');
  return id;
}

// F2 du dossier encore en création, complétée plutôt que doublée : la plus récente.
export function reusableSaleOrder(orders: Sales_order[] | null | undefined, rentalOrderId: number): Sales_order | null {
  return (orders ?? [])
    .filter((o) => o.doc_type === DocType.FACTURE && o.state === SalesOrderState.CREATED && Number(o.data?.rental_order_id) === rentalOrderId)
    .sort((a, b) => b.id - a.id)[0] ?? null;
}

// Lignes ajoutées à la F2 relue, numérotées après les siennes.
export function appendSaleLines(fg: FormGroup, lines: FormGroup[]): void {
  const arr = fg.get('lines') as FormArray;
  let seq = arr.controls.reduce((m, c) => Math.max(m, Number(c.get('seq')?.value) || 0), 0);
  const id = Number(fg.get('id')?.value) || 0;
  for (const l of lines) { l.patchValue({ id: 0, id_order: id, seq: ++seq }); arr.push(l); }
}

export interface SaleOrderApi {
  list: (rentalOrderId: number) => Observable<Sales_order[]>;
  read: (id: number) => Observable<FormGroup | undefined>;
  post: (typ: number, fg: FormGroup) => Observable<any>;
}

// Produits vendus du dossier : ajoutés à sa F2 en création, relue juste avant l'écriture, sinon une F2 neuve ; rend son id ou lève le message du serveur.
export async function placeSaleLines(api: SaleOrderApi, head: SaleOrderHead, user: number | null, store: number | null, lines: FormGroup[]): Promise<number> {
  const open = reusableSaleOrder(await firstValueFrom(api.list(head.id)), head.id);
  const fg = open ? await firstValueFrom(api.read(open.id)) : undefined;
  if (open && fg && fg.get('state')?.value === SalesOrderState.CREATED && Number(fg.get('data')?.value?.rental_order_id) === head.id) {
    appendSaleLines(fg, lines);
    prepareRentalSave(fg, null, store ?? 0);
    const response: any = await firstValueFrom(api.post(EVENTTYP_UPDATE, fg));
    if (!response?.success) throw new Error(response?.msg || response?.error_msg || `commande de vente ${open.id} non complétée`);
    return open.id;
  }
  return createSaleOrder((nfg) => api.post(EVENTTYP_INSERT, nfg), head, user, store, lines);
}
